/// <reference path='../../shell/scripts/IResourceEvent.ts' />
/// <reference path='../../shell/scripts/IContextAction.ts' />
/// <reference path='../../shell/scripts/Shell.d.ts' />
import ResourceEventClass from './ResourceEvent';
import DapiProxy from "./DapiProxy";
/**
 * public api of the shell.
 */
export default class Dapi {
    /**
     *
     * @param delegate Shell or Dapi
     * @param toAbsoluteUri function which returns an absolute uri for the given uri interpreted as relative to the base uri (https://developers.whatwg.org/urls.html#base-urls)
     * @param rootResourceId id of outemost resource
     * @param isShell describs the type (Shell or Dapi) of the delegate parameter
     * @returns {}
     */
    constructor(delegate, toAbsoluteUri, rootResourceId, isShell = true) {
        // ReSharper disable once InconsistentNaming
        // export ResourceEvent class to provide clients which don't consume the dapi as a module with the ability to use the ResourceEvent class for things like x instanceof dapi.ResourceEvent
        this.ResourceEvent = ResourceEventClass;
        if (!isShell) {
            this._parentDapi = delegate;
            if (!this._parentDapi._shell && !this._parentDapi.dapiProxy) {
                console.warn('The embedding resource uses an obsolete d.api ( < v2.12.0).');
            }
        }
        else {
            this._shell = delegate;
        }
        this.dapiProxy = new DapiProxy(this);
        this._toAbsoluteUri = toAbsoluteUri;
        this._rootResourceId = rootResourceId;
    }
    /**
     * Navigate to resource identified by href. The following values for href have a special meaning:
     * - 'dapi_blank' replace the current main resource with an empty resource. That is show a blank page.
     * @param href absolute or relative (cf. https://developers.whatwg.org/urls.html#base-urls) URI of target resource or 'dapi_blank'
     * @returns {}
     */
    navigate(href) {
        if (this._shell) {
            this._shell.navigate(href === "dapi_blank" ? "about:blank" : href ? this._toAbsoluteUri(href) : href, this._rootResourceId);
        }
        else {
            if (!!this._parentDapi.dapiProxy) {
                this._parentDapi.dapiProxy.navigate(href);
            }
            else {
                console.info('The embedding resource uses an obsolete d.api ( < v2.12.0).');
            }
        }
    }
    /**
     * Opens a global dialog with content referenced by href.
     *  @param {string} href absolute or relative (cf. https://developers.whatwg.org/urls.html#base-urls) URI of dialog content
     *  @param {number} height of the dialog (see also absoluteValues)
     *  @param {number} width of the dialog (see also absoluteValues)
     *  @param {boolean} [absoluteValues] set true if values for height and width are to be interpreted as absolute values, namely in pixel and not in percent
     */
    openDialog(href, height, width, absoluteValues) {
        if (this._shell) {
            this._shell.openDialog(href ? this._toAbsoluteUri(href) : href, height, width, absoluteValues, this._rootResourceId);
        }
        else {
            if (!!this._parentDapi.dapiProxy) {
                this._parentDapi.dapiProxy.openDialog(href, height, width, absoluteValues);
            }
            else {
                console.info('The embedding resource uses an obsolete d.api ( < v2.12.0).');
            }
        }
    }
    /**
     * Closes the global dialog when it is open, otherwise nothing will happen.
     */
    closeDialog() {
        if (this._shell) {
            this._shell.closeDialog(this._rootResourceId);
        }
        else {
            if (!!this._parentDapi.dapiProxy) {
                this._parentDapi.dapiProxy.closeDialog();
            }
            else {
                console.info('The embedding resource uses an obsolete d.api ( < v2.12.0).');
            }
        }
    }
    /**
     * Downloads the referenced file.
     * @param href absolute or relative (cf. https://developers.whatwg.org/urls.html#base-urls) download URI
     * @returns {}
     */
    download(href) {
        if (this._shell) {
            this._shell.download(href ? this._toAbsoluteUri(href) : href, this._rootResourceId);
        }
        else {
            if (!!this._parentDapi.dapiProxy) {
                this._parentDapi.dapiProxy.download(href);
            }
            else {
                console.info('The embedding resource uses an obsolete d.api ( < v2.12.0).');
            }
        }
    }
    /**
     * Replace the state of the resource.
     * @param replaceUri
     * @returns {}
     */
    replaceState(replaceUri) {
        if (this._shell) {
            this._shell.replaceState(this._toAbsoluteUri(replaceUri), this._rootResourceId);
        }
        else {
            if (!!this._parentDapi.dapiProxy) {
                this._parentDapi.dapiProxy.replaceState(replaceUri);
            }
            else {
                console.info('The embedding resource uses an obsolete d.api ( < v2.12.0).');
            }
        }
    }
    /**
     * Publishes actions and resources which are available in the current context/state of this resource.
     * Note: The URIs inside the contextActions elements can be absolute or relative to document base uri (cf. https://developers.whatwg.org/urls.html#base-urls)
     * @param contextActions Array of IContextAction
     * @returns {}
     */
    setContextActions(contextActions) {
        if (this._shell) {
            this._shell.setContextActions(contextActions ? this.convertToContextActionsWithAbsoluteUri(contextActions) : contextActions, this._rootResourceId);
        }
        else {
            if (!!this._parentDapi.dapiProxy) {
                this._parentDapi.dapiProxy.setContextActions(contextActions);
            }
            else {
                console.info('The embedding resource uses an obsolete d.api ( < v2.12.0).');
            }
        }
    }
    /**
     * Publishes the title of this resource to the shell.
     * @param title
     * @returns {}
     */
    publishTitle(title) {
        if (this._shell) {
            this._shell.publishTitle(title, this._rootResourceId);
        }
        else {
            if (!!this._parentDapi.dapiProxy) {
                this._parentDapi.dapiProxy.publishTitle(title);
            }
            else {
                console.info('The embedding resource uses an obsolete d.api ( < v2.12.0).');
            }
        }
    }
    /**
     * Publishes the location of this resource to the shell.
     * @param uri current location of this resource
     * @returns {}
     */
    publishLocation(uri) {
        if (this._shell) {
            this._shell.publishLocation(uri, this._rootResourceId);
        }
        else {
            if (!!this._parentDapi.dapiProxy) {
                this._parentDapi.dapiProxy.publishLocation(uri);
            }
            else {
                console.info('The embedding resource uses an obsolete d.api ( < v2.12.0).');
            }
        }
    }
    /**
     * Returns true if this resource is current main resource.
     * @returns {}
     */
    isMainResource() {
        if (this._shell) {
            return this._shell.isMainResource(this._rootResourceId);
        }
        else {
            if (!!this._parentDapi.dapiProxy) {
                return this._parentDapi.dapiProxy.isMainResource();
            }
            else {
                console.info('The embedding resource uses an obsolete d.api ( < v2.12.0).');
                return false;
            }
        }
    }
    /**
     * @param callback will be called if resource should be closed or refreshed
     * @deprecated please use setClosable(isClosable: boolean): void; and setInterruptNavigationCallback(callback: (finishNavigation: () => void) => void, originatorId: Object): void; instead
     */
    setInterruptCloseCallback(callback) {
        if (this._shell) {
            console.warn("setInterruptCloseCallback() is deprecated! Please use setClosable() and setInterruptNavigationCallback() instead.\n" +
                "setInterruptCloseCallback() will be removed with the next major release.");
            this._shell.setInterruptCloseCallback(callback, this._rootResourceId);
        }
        else {
            console.warn("setInterruptCloseCallback() is deprecated! Please use setClosable() and setInterruptNavigationCallback() instead.\n" +
                "setInterruptCloseCallback() will be removed with the next major release.");
        }
    }
    /**
     * If resource must not be close set call it with false.
     * Note: To interrupt the navigation it is necessary to register an interruptNavigationCallback.
     * Please use setInterruptNavigationCallback(callback: (finishNavigation: () => void) => void, originatorId: Object): void
     * NOT setInterruptCloseCallback(callback: () => boolean, originatorId: Object): void
     * @param isClosable
     */
    setClosable(isClosable) {
        if (this._shell) {
            this._shell.setClosable(isClosable, this._rootResourceId);
        }
        else {
            if (!!this._parentDapi.dapiProxy) {
                this._parentDapi.dapiProxy.setClosable(isClosable);
            }
            else {
                console.info('The embedding resource uses an obsolete d.api ( < v2.12.0).');
            }
        }
    }
    /**
     * The registered callback will be invoked if resource is not closable. The initial navigation could be finish by
     * invoking the 'finishNavigation' callback.
     * @param callback
     */
    setInterruptNavigationCallback(callback) {
        if (this._shell) {
            this._shell.setInterruptNavigationCallback(callback, this._rootResourceId);
        }
        else {
            if (!!this._parentDapi.dapiProxy) {
                this._parentDapi.dapiProxy.setInterruptNavigationCallback(callback);
            }
            else {
                console.info('The embedding resource uses an obsolete d.api ( < v2.12.0).');
            }
        }
    }
    convertToContextActionsWithAbsoluteUri(contextActions) {
        const absContextActions = new Array();
        for (let i = 0; i < contextActions.length; i++) {
            const contextAction = contextActions[i];
            absContextActions.push({
                href: contextAction.href ? this._toAbsoluteUri(contextAction.href) : contextAction.href,
                callback: contextAction.callback,
                callbackName: contextAction.callbackName,
                callbackArgs: contextAction.callbackArgs,
                title: contextAction.title,
                icon: contextAction.icon ? this._toAbsoluteUri(contextAction.icon) : contextAction.icon
            });
        }
        return absContextActions;
    }
    /**
     * Dispatch an event to all registered listeners.
     * @param event
     * @returns {}
     */
    dispatchResourceEvent(event) {
        if (this._shell) {
            this._shell.dispatchResourceEvent(event, this._rootResourceId);
        }
        else {
            if (!!this._parentDapi.dapiProxy) {
                this._parentDapi.dapiProxy.dispatchResourceEvent(event);
            }
            else {
                console.info('The embedding resource uses an obsolete d.api ( < v2.12.0).');
            }
        }
    }
    /**
     * Listen to events of type eventName
     * @param eventName
     * @param callback
     * @returns {}
     */
    addResourceEventListener(eventName, callback) {
        if (this._shell) {
            this._shell.addResourceEventListener(eventName, callback, this._rootResourceId);
        }
        else {
            if (!!this._parentDapi.dapiProxy) {
                this._parentDapi.dapiProxy.addResourceEventListener(eventName, callback);
            }
            else {
                console.info('The embedding resource uses an obsolete d.api ( < v2.12.0).');
            }
        }
    }
    /**
     * Activates single resource mode.
     * In single resource mode the shell displays only one resource which gets the maximum amount of space.
     */
    activateSingleResourceMode() {
        if (this._shell) {
            this._shell.activateSingleResourceMode(this._rootResourceId);
        }
        else {
            if (!!this._parentDapi.dapiProxy) {
                this._parentDapi.dapiProxy.activateSingleResourceMode();
            }
            else {
                console.info('The embedding resource uses an obsolete d.api ( < v2.12.0).');
            }
        }
    }
    /**
     * Deactivates single resource mode.
     * In single resource mode the shell displays only one resource which gets the maximum amount of space.
     */
    deactivateSingleResourceMode() {
        if (this._shell) {
            this._shell.deactivateSingleResourceMode(this._rootResourceId);
        }
        else {
            if (!!this._parentDapi.dapiProxy) {
                this._parentDapi.dapiProxy.deactivateSingleResourceMode();
            }
            else {
                console.info('The embedding resource uses an obsolete d.api ( < v2.12.0).');
            }
        }
    }
    /**
     *  Closes the mainresource if it is called from the mainresource
     */
    closeResourceIfMainResource() {
        if (this._shell) {
            // TODO prüfem, ob der Check überflüssig ist
            if (!this.isMainResource())
                return;
            this._shell.closeMainResource(this._rootResourceId);
        }
        else {
            if (!!this._parentDapi.dapiProxy) {
                this._parentDapi.dapiProxy.closeResourceIfMainResource();
            }
            else {
                console.info('The embedding resource uses an obsolete d.api ( < v2.12.0).');
            }
        }
    }
    /**
     * The registered callback will be invoked after the resource has been displayed or hidden.
     * @param callback
     */
    setResourceVisibilityChangedCallback(callback) {
        if (this._shell) {
            this._shell.setResourceVisibilityChangedCallback(callback, this._rootResourceId);
        }
        else {
            if (!!this._parentDapi.dapiProxy) {
                this._parentDapi.dapiProxy.setResourceVisibilityChangedCallback(callback);
            }
            else {
                console.info('The embedding resource uses an obsolete d.api ( < v2.12.0).');
            }
        }
    }
}
